import klarnaUtils from 'utils/Klarna';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';

/**
 * Shared helpers for Klarna and AfterPay widget loading in Frictionless Checkout Release 4
 */

/**
 * Returns user-friendly error message based on error type for Klarna widget failures
 */
export const getKlarnaErrorMessage = (error, locales) => {
    // network errors
    if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
        return locales.klarnaNetworkError || 'Network error. Please check your connection and try again.';
    }

    // fallback to generic error
    return locales.klarnaApiErrorFallback || 'Unable to load Klarna payment information. Please try again or choose another payment method.';
};

/**
 * Logs Klarna errors to analytics
 */
export const logKlarnaError = (errorType, errorMessage) => {
    try {
        FrictionlessCheckoutBindings.setKlarnaErrorAnalytics && FrictionlessCheckoutBindings.setKlarnaErrorAnalytics(errorType, errorMessage);
    } catch (e) {
        Sephora.logger.error('Failed to log Klarna error analytics', e);
    }
};

/**
 * Factory function that creates a loadKlarnaWidget handler
 * This DRYs up the duplicate logic between Payment.ctrlr.jsx and PaymentMethodList.ctrlr.jsx
 *
 * @param {Object} config - Configuration object
 * @param {string} config.widgetElementId - DOM element ID for Klarna widget container
 * @param {Function} config.setState - Component setState function
 * @param {Function} config.klarnaSetReady - Redux action to set Klarna ready state
 * @param {Object} config.locales - Localization strings
 * @returns {Function} Async function that loads Klarna widget
 */
export const createKlarnaWidgetLoader = ({ widgetElementId, setState, klarnaSetReady, locales }) => {
    return async () => {
        // timeout fallback if SDK never completes
        const timeoutId = setTimeout(() => {
            setState({
                isKlarnaLoading: false,
                klarnaError: locales.klarnaTimeoutError || 'Request timed out. Please try again.'
            });
            klarnaSetReady && klarnaSetReady(false);
            logKlarnaError('timeout', 'Klarna widget load timeout');
        }, 10000);

        try {
            // hit backend to get session token
            // The group will be finalized (isComplete=true) only when user clicks Save and Continue
            const { session } = await klarnaUtils.initPaymentGroup();

            if (!session?.clientToken) {
                throw new Error('No client token received from Klarna');
            }

            // load SDK widget into DOM
            await klarnaUtils.load(widgetElementId, session.clientToken);

            clearTimeout(timeoutId);
            // Don't update klarnaApiData to avoid re-render that destroys the DOM element
            setState({
                isKlarnaLoading: false
            });
        } catch (error) {
            clearTimeout(timeoutId);
            const errorMessage = getKlarnaErrorMessage(error, locales);

            setState({
                isKlarnaLoading: false,
                klarnaError: errorMessage
            });
            klarnaSetReady && klarnaSetReady(false);
            logKlarnaError('load_failure', error?.message || 'Unknown error');
        }
    };
};
